import { useState, useRef, useEffect } from "react";
import { Layout } from "@/components/layout";
import { useAuth } from "@/contexts/auth";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  LifeBuoy, ChevronDown, ChevronUp, Inbox, Clock,
  CheckCircle2, Loader2, Send, User, Shield, Trash2, AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

interface SupportTicket {
  id: number;
  userId: number | null;
  userEmail: string;
  userName: string;
  category: string;
  subject: string;
  message: string;
  status: "open" | "in_progress" | "resolved";
  adminNote: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TicketMessage {
  id: number;
  ticketId: number;
  senderRole: "admin" | "user";
  senderName: string;
  message: string;
  createdAt: string;
}

interface PublicContact {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  subject: string;
  message: string;
  ip: string | null;
  seen_at: string | null;
  created_at: string;
}

type StatusFilter = "all" | "open" | "in_progress" | "resolved";

async function fetchWithAuth(path: string, options?: RequestInit) {
  const baseUrl = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";
  const token = localStorage.getItem("token");
  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options?.headers ?? {}),
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const STATUS_CONFIG = {
  open: { label: "OPEN", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  in_progress: { label: "IN PROGRESS", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  resolved: { label: "RESOLVED", color: "bg-primary/20 text-primary border-primary/30" },
};

const CATEGORY_COLORS: Record<string, string> = {
  "Bug Report": "bg-destructive/10 text-destructive border-destructive/30",
  "Question about Study Content": "bg-violet-500/10 text-violet-400 border-violet-500/30",
  "Exam / Session Issue": "bg-orange-500/10 text-orange-400 border-orange-500/30",
  "Account & Access": "bg-blue-500/10 text-blue-400 border-blue-500/30",
  "Feature Request": "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  "Other": "bg-secondary text-muted-foreground border-border",
};

function ConversationThread({ ticket }: { ticket: SupportTicket }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [noteValue, setNoteValue] = useState(ticket.adminNote ?? "");
  const [savingNote, setSavingNote] = useState(false);
  const { toast: replyToast } = useToast();
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: messages = [], isLoading } = useQuery<TicketMessage[]>({
    queryKey: ["ticket-messages", ticket.id],
    queryFn: () => fetchWithAuth(`/api/support/tickets/${ticket.id}/messages`),
    refetchInterval: 15000,
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const updateTicket = useMutation({
    mutationFn: (body: { status?: string; adminNote?: string }) =>
      fetchWithAuth(`/api/admin/support/tickets/${ticket.id}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-tickets"] }),
  });

  const sendReply = async () => {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await fetchWithAuth(`/api/admin/support/tickets/${ticket.id}/messages`, {
        method: "POST",
        body: JSON.stringify({ message: reply.trim() }),
      });
      setReply("");
      qc.invalidateQueries({ queryKey: ["ticket-messages", ticket.id] });
      qc.invalidateQueries({ queryKey: ["admin-tickets"] });
      replyToast({ title: "Reply sent", description: `Email notification dispatched to ${ticket.userEmail}.`, duration: 5000 });
    } finally {
      setSending(false);
    }
  };

  const handleSaveNote = async () => {
    setSavingNote(true);
    await updateTicket.mutateAsync({ adminNote: noteValue });
    setSavingNote(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) sendReply();
  };

  return (
    <div className="border-t border-border/50 bg-secondary/10 space-y-5 p-4">

      {/* Status controls */}
      <div>
        <p className="text-xs font-mono text-muted-foreground/60 uppercase tracking-wider mb-2">Status</p>
        <div className="flex gap-2 flex-wrap">
          {(["open", "in_progress", "resolved"] as const).map(s => (
            <Button
              key={s}
              size="sm"
              variant="outline"
              disabled={ticket.status === s || updateTicket.isPending}
              onClick={() => updateTicket.mutate({ status: s })}
              className={cn(
                "font-mono text-xs h-8 px-3",
                ticket.status === s ? STATUS_CONFIG[s].color + " border opacity-70 cursor-default" : ""
              )}
            >
              {STATUS_CONFIG[s].label}
            </Button>
          ))}
        </div>
      </div>

      {/* Original message */}
      <div>
        <p className="text-xs font-mono text-muted-foreground/60 uppercase tracking-wider mb-2">Original Message</p>
        <div className="bg-card border border-border rounded-md p-3">
          <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{ticket.message}</p>
          <p className="text-xs text-muted-foreground/50 font-mono mt-2">
            {ticket.userName} · {new Date(ticket.createdAt).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Conversation thread */}
      <div>
        <p className="text-xs font-mono text-muted-foreground/60 uppercase tracking-wider mb-2">
          Conversation {messages.length > 0 ? `(${messages.length})` : ""}
        </p>

        {isLoading ? (
          <p className="text-xs text-muted-foreground font-mono animate-pulse">Loading messages...</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-muted-foreground/50 font-mono italic">No replies yet — start the conversation below.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {messages.map(msg => {
              const isAdmin = msg.senderRole === "admin";
              return (
                <div
                  key={msg.id}
                  className={cn(
                    "flex gap-2.5 items-start",
                    isAdmin ? "flex-row-reverse" : "flex-row"
                  )}
                >
                  <div className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5",
                    isAdmin ? "bg-primary/20" : "bg-secondary"
                  )}>
                    {isAdmin
                      ? <Shield className="w-3.5 h-3.5 text-primary" />
                      : <User className="w-3.5 h-3.5 text-muted-foreground" />
                    }
                  </div>
                  <div className={cn(
                    "max-w-[80%] rounded-lg px-3 py-2 text-sm leading-relaxed",
                    isAdmin
                      ? "bg-primary/10 border border-primary/20 text-foreground rounded-tr-none"
                      : "bg-card border border-border text-foreground rounded-tl-none"
                  )}>
                    <p className="whitespace-pre-wrap">{msg.message}</p>
                    <p className={cn(
                      "text-xs mt-1 font-mono",
                      isAdmin ? "text-primary/50 text-right" : "text-muted-foreground/50"
                    )}>
                      {msg.senderName} · {new Date(msg.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}

        {/* Reply box */}
        <div className="mt-3 space-y-2">
          <Textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your reply… (Ctrl+Enter to send)"
            className="text-sm min-h-[80px] resize-none bg-background"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground/40 font-mono">Visible to the user</p>
            <Button
              size="sm"
              disabled={!reply.trim() || sending}
              onClick={sendReply}
              className="font-mono text-xs gap-2"
            >
              <Send className="w-3.5 h-3.5" />
              {sending ? "SENDING..." : "SEND REPLY"}
            </Button>
          </div>
        </div>
      </div>

      {/* Internal note */}
      <div className="border-t border-border/40 pt-4">
        <p className="text-xs font-mono text-muted-foreground/60 uppercase tracking-wider mb-2">
          Internal Note <span className="text-muted-foreground/30 normal-case">(not visible to user)</span>
        </p>
        <Textarea
          value={noteValue}
          onChange={e => setNoteValue(e.target.value)}
          placeholder="Private note for admin reference only"
          className="text-sm min-h-[70px] resize-none bg-background"
        />
        <div className="flex justify-end mt-2">
          <Button
            size="sm"
            variant="outline"
            disabled={savingNote}
            onClick={handleSaveNote}
            className="font-mono text-xs"
          >
            {savingNote ? "SAVING..." : "SAVE NOTE"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminTickets() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  // ── ALL hooks above the role guard (React rules of hooks) ───────────────────
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [expandedContactId, setExpandedContactId] = useState<number | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      fetchWithAuth(`/api/admin/support/tickets/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmDeleteId(null);
      setExpandedId(null);
      qc.invalidateQueries({ queryKey: ["admin-tickets"] });
    },
  });

  const { data: contactsRaw, isLoading: contactsLoading } = useQuery<PublicContact[]>({
    queryKey: ["admin-public-contacts"],
    queryFn: async () => {
      const r = (await fetchWithAuth("/api/admin/public-contacts")) as { contacts?: PublicContact[] } | PublicContact[];
      if (Array.isArray(r)) return r;
      const list = r?.contacts;
      return Array.isArray(list) ? list : [];
    },
    refetchInterval: 30000,
  });
  const contacts = Array.isArray(contactsRaw) ? contactsRaw : [];

  const markSeenMutation = useMutation({
    mutationFn: (id: number) =>
      fetchWithAuth(`/api/admin/public-contacts/${id}/mark-seen`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-public-contacts"] });
      qc.invalidateQueries({ queryKey: ["sidebar-counts"] });
    },
  });

  const markAllSeenMutation = useMutation({
    mutationFn: () =>
      fetchWithAuth("/api/admin/public-contacts/mark-all-seen", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-public-contacts"] });
      qc.invalidateQueries({ queryKey: ["sidebar-counts"] });
    },
  });

  // Always fetch ALL tickets — filter client-side so tab counts are always correct
  const { data: allTickets = [], isLoading } = useQuery<SupportTicket[]>({
    queryKey: ["admin-tickets"],
    queryFn: () => fetchWithAuth("/api/admin/support/tickets"),
    refetchInterval: 30000,
  });

  // ── Role guard — after all hooks ────────────────────────────────────────────
  if (!["admin", "master_admin", "moderator"].includes(user?.role ?? "")) {
    setLocation("/");
    return null;
  }

  const unseenContacts = contacts.filter(c => !c.seen_at);

  const toggleContact = (id: number) => {
    if (expandedContactId !== id) {
      // Auto-mark as seen when first opened
      const contact = contacts.find(c => c.id === id);
      if (contact && !contact.seen_at) {
        markSeenMutation.mutate(id);
      }
    }
    setExpandedContactId(prev => (prev === id ? null : id));
  };

  const open = allTickets.filter(t => t.status === "open");
  const inProgress = allTickets.filter(t => t.status === "in_progress");
  const resolved = allTickets.filter(t => t.status === "resolved");

  const visibleTickets =
    statusFilter === "all" ? allTickets :
    statusFilter === "open" ? open :
    statusFilter === "in_progress" ? inProgress :
    resolved;

  const tabs: { key: StatusFilter; label: string; count: number; activeClass: string }[] = [
    { key: "open", label: "Open", count: open.length, activeClass: "border-yellow-400 text-yellow-400 bg-yellow-500/10" },
    { key: "in_progress", label: "In Progress", count: inProgress.length, activeClass: "border-blue-400 text-blue-400 bg-blue-500/10" },
    { key: "resolved", label: "Resolved", count: resolved.length, activeClass: "border-primary text-primary bg-primary/10" },
    { key: "all", label: "All", count: allTickets.length, activeClass: "border-foreground/40 text-foreground bg-secondary" },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <div className="flex items-center gap-3">
            <LifeBuoy className="w-7 h-7 text-primary" />
            <h1 className="text-3xl font-bold tracking-tight text-foreground">Support Tickets</h1>
          </div>
          <p className="text-muted-foreground font-mono text-sm mt-1">INCOMING USER MESSAGES & ISSUES</p>
        </div>

        {/* Pre-Login Contacts Section */}
        <div className="border border-amber-500/20 rounded-md bg-amber-500/5">
          <div className="flex items-center justify-between px-4 py-3 border-b border-amber-500/15">
            <div className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-amber-400" />
              <span className="font-mono font-semibold text-amber-400 tracking-wide text-sm">PRE-LOGIN CONTACTS</span>
              {unseenContacts.length > 0 && (
                <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                  {unseenContacts.length} UNREAD
                </span>
              )}
            </div>
            {unseenContacts.length > 0 && (
              <button
                onClick={() => markAllSeenMutation.mutate()}
                disabled={markAllSeenMutation.isPending}
                className="text-xs font-mono text-amber-400/60 hover:text-amber-400 transition-colors disabled:opacity-40"
              >
                {markAllSeenMutation.isPending ? "MARKING..." : "MARK ALL READ"}
              </button>
            )}
          </div>

          {contactsLoading ? (
            <div className="px-4 py-6 text-amber-400/60 font-mono text-xs animate-pulse">LOADING CONTACTS...</div>
          ) : contacts.length === 0 ? (
            <div className="px-4 py-8 text-center text-muted-foreground font-mono text-xs">No pre-login messages yet.</div>
          ) : (
            <div className="divide-y divide-amber-500/10">
              {contacts.map(contact => {
                const isOpen = expandedContactId === contact.id;
                const isUnseen = !contact.seen_at;
                return (
                  <div key={contact.id} className={cn("transition-colors", isUnseen && !isOpen ? "bg-amber-500/5" : "")}>
                    <button
                      className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-amber-500/10 transition-colors"
                      onClick={() => toggleContact(contact.id)}
                    >
                      <div className="flex-1 min-w-0 space-y-0.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          {isUnseen && (
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0 mt-0.5" />
                          )}
                          <span className="font-semibold text-sm text-foreground truncate">{contact.subject}</span>
                          <span className="text-xs text-muted-foreground font-mono ml-auto shrink-0">
                            {new Date(contact.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {contact.name} · {contact.email}
                          {contact.phone && <> · {contact.phone}</>}
                        </div>
                      </div>
                      <div className="shrink-0 text-amber-400/50 mt-0.5">
                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 pt-1 space-y-2">
                        <div className="text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap border-l-2 border-amber-500/30 pl-3">
                          {contact.message}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground/50 font-mono">
                          {contact.ip && <span>IP: {contact.ip}</span>}
                          <span>Received: {new Date(contact.created_at).toLocaleString()}</span>
                          {contact.seen_at && <span className="text-amber-400/50">Read: {new Date(contact.seen_at).toLocaleString()}</span>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: "OPEN", count: open.length, icon: Clock, color: "text-yellow-400" },
            { label: "IN PROGRESS", count: inProgress.length, icon: Loader2, color: "text-blue-400" },
            { label: "RESOLVED", count: resolved.length, icon: CheckCircle2, color: "text-primary" },
          ].map(({ label, count, icon: Icon, color }) => (
            <Card key={label} className="border-border bg-card">
              <CardContent className="p-4 flex items-center gap-3">
                <Icon className={cn("w-6 h-6", color)} />
                <div>
                  <div className={cn("text-2xl font-bold font-mono", color)}>{count}</div>
                  <div className="text-xs text-muted-foreground font-mono tracking-wider">{label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 flex-wrap border-b border-border">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={cn(
                "px-4 py-2 text-sm font-mono font-medium border-b-2 -mb-px transition-colors",
                statusFilter === tab.key
                  ? tab.activeClass + " border-b-2"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {tab.label}
              <span className={cn(
                "ml-2 text-xs px-1.5 py-0.5 rounded-full font-mono",
                statusFilter === tab.key ? "" : "bg-secondary text-muted-foreground"
              )}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Ticket List */}
        {isLoading ? (
          <div className="text-primary font-mono text-sm animate-pulse tracking-widest">LOADING TICKETS...</div>
        ) : visibleTickets.length === 0 ? (
          <Card className="border-border">
            <CardContent className="p-16 text-center">
              <Inbox className="w-14 h-14 text-muted-foreground/20 mx-auto mb-4" />
              <p className="text-muted-foreground font-mono text-sm">
                No {statusFilter === "all" ? "" : statusFilter.replace("_", " ")} tickets.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {visibleTickets.map(ticket => {
              const isExpanded = expandedId === ticket.id;
              const cfg = STATUS_CONFIG[ticket.status];
              const catColor = CATEGORY_COLORS[ticket.category] ?? CATEGORY_COLORS["Other"];

              const isConfirmingDelete = confirmDeleteId === ticket.id;
              return (
                <div key={ticket.id} className="border border-border rounded-md bg-card/50 overflow-hidden">
                  <div className="flex items-stretch">
                    {/* Main expand button */}
                    <button
                      className="flex-1 text-left p-4 flex items-start gap-3 hover:bg-secondary/20 transition-colors min-w-0"
                      onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                    >
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={cn("font-mono text-xs border", cfg.color)}>{cfg.label}</Badge>
                          <Badge className={cn("font-mono text-xs border", catColor)}>{ticket.category}</Badge>
                          <span className="text-xs text-muted-foreground font-mono ml-auto">
                            #{ticket.id} · {new Date(ticket.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="font-semibold text-foreground truncate">{ticket.subject}</div>
                        <div className="text-xs text-muted-foreground font-mono">
                          {ticket.userName} · {ticket.userEmail}
                        </div>
                      </div>
                      <div className="shrink-0 text-muted-foreground mt-1">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </button>

                    {/* Delete button — admin and moderator */}
                    {(["admin", "master_admin", "moderator"].includes(user?.role ?? "")) && (
                      <div className="flex items-center px-3 shrink-0 border-l border-border/40">
                        <button
                          title="Delete ticket"
                          onClick={() => setConfirmDeleteId(isConfirmingDelete ? null : ticket.id)}
                          className={cn(
                            "p-1.5 rounded transition-colors",
                            isConfirmingDelete
                              ? "text-red-400 bg-red-500/10"
                              : "text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                          )}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Delete confirmation */}
                  {isConfirmingDelete && (
                    <div className="border-t border-red-500/20 bg-red-500/5 px-4 py-3 flex items-center gap-3">
                      <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                      <p className="text-xs text-red-400 font-mono flex-1">
                        Permanently delete this ticket and all messages? This cannot be undone.
                      </p>
                      <div className="flex gap-2 shrink-0">
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-xs font-mono px-2.5 py-1 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => deleteMutation.mutate(ticket.id)}
                          disabled={deleteMutation.isPending}
                          className="text-xs font-mono px-2.5 py-1 rounded border border-red-500/40 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                        >
                          {deleteMutation.isPending ? "Deleting…" : "Delete"}
                        </button>
                      </div>
                    </div>
                  )}

                  {isExpanded && <ConversationThread key={ticket.id} ticket={ticket} />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
